import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useSearch } from '@tanstack/react-router'
import { ConsoleError, type Region } from '@/api'
import { useRegions, useVote } from '@/api/hooks'
import { copy } from '@/copy'
import { PageHead } from '@/components/Shell'
import { Failed, Loading } from '@/components/States'
import { useConsole } from '@/lib/store'
import { leaders, note, ranked, width, words } from '@/lib/regions'
import { useTitle } from '@/lib/useTitle'

// Regions (ADR-0029): where Purlview should run next. The account votes once
// per region with one click; the board shows every region's share as a bar
// scaled to the leader, no numbers, and says each in words to a screen
// reader. purlview.com/regions/ links here with ?region= to pick one out.
export function Regions() {
  useTitle(copy.titles.regions)
  const { board: regions, late } = useRegions()
  const vote = useVote()
  const announce = useConsole((s) => s.announce)
  const search = useSearch({ strict: false }) as { region?: unknown }
  const picked = typeof search.region === 'string' ? search.region : undefined
  const [order, setOrder] = useState<string[] | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [done, setDone] = useState<{ name: string; failed?: string } | null>(null)
  const pickedButton = useRef<HTMLButtonElement>(null)

  const list = regions.data?.regions
  // Ranked once, on arrival: later answers move the bars, never the rows, so
  // a button never moves from under a pointer or the keyboard.
  useEffect(() => {
    if (list && !order) setOrder(ranked(list))
  }, [list, order])
  // Arriving from purlview.com with a region picked: its Vote takes focus,
  // and the live region says where the person is, since moving focus skips
  // the heading a screen reader would otherwise read first.
  useEffect(() => {
    if (order && pickedButton.current && list) {
      const r = list.find((x) => x.id === picked)
      pickedButton.current.focus({ preventScroll: true })
      pickedButton.current.scrollIntoView({ block: 'center' })
      if (r) announce(r.voted ? copy.regions.pickedVoted(r.name) : copy.regions.picked(r.name))
    }
    // Once, when the board is first drawn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order])

  const cast = async (r: Region) => {
    if (busy || r.voted) return
    setBusy(r.id)
    setDone(null)
    try {
      await vote.mutateAsync(r.id)
      announce(copy.regions.counted(r.name))
      setDone({ name: r.name })
    } catch (e) {
      const line = e instanceof ConsoleError && e.code === 'wait' ? copy.regions.wait : copy.regions.failedVote
      announce(line)
      setDone({ name: r.name, failed: line })
    }
    setBusy(null)
  }

  let body: ReactNode
  if (!list && regions.isError) body = <Failed what={copy.regions.failed} onRetry={() => void regions.refetch()} busy={regions.isFetching} />
  else if (!list || !order) body = <Loading rows={4} />
  else {
    const top = leaders(list)
    const byId = new Map(list.map((r) => [r.id, r]))
    body = (
      <section className="card overflow-hidden" aria-labelledby="regions-board">
        <h2 id="regions-board" className="visually-hidden">
          {copy.regions.heading}
        </h2>
        <p className="m-0 flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-rule px-5 py-4">
          <span className="inline-flex items-center gap-2 font-mono text-[12px] uppercase tracking-[0.1em] text-working">
            <span className="inline-block h-2 w-2 rounded-full bg-working" aria-hidden="true" />
            {copy.regions.live}
          </span>
          <strong className="font-medium">{copy.regions.home}</strong>
          <span className="text-secondary">{copy.regions.homeNote}</span>
        </p>
        <ol className="m-0 list-none p-0">
          {order.map((id) => {
            const r = byId.get(id)
            if (!r) return null
            const lead = top.includes(id)
            const place = lead ? `${top.length === 1 ? copy.regions.outInFront : copy.regions.levelAtTop}, ` : ''
            return (
              <li
                key={id}
                className={`m-0 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-2 border-t border-rule px-5 py-3 first:border-t-0 ${id === picked && !r.voted ? 'bg-thread/10' : ''}`}
              >
                <h3 className="m-0 min-w-0 text-[17px] font-medium leading-6 [overflow-wrap:anywhere]">
                  {r.name}
                  {r.voted && <span className="ml-2.5 inline-block h-1.5 w-1.5 rounded-full bg-thread align-middle" aria-hidden="true" />}
                  <span className="visually-hidden">
                    , {place}
                    {words(r.share, r.voted)}
                  </span>
                </h3>
                <button
                  type="button"
                  ref={id === picked ? pickedButton : undefined}
                  className={`action row-span-2 min-w-[96px] ${r.voted ? 'border-thread text-accent' : ''}`}
                  aria-disabled={r.voted || busy !== null || undefined}
                  onClick={() => void cast(r)}
                >
                  {r.voted ? copy.regions.voted : busy === id ? copy.regions.voting : copy.regions.vote}
                  <span className="visually-hidden"> {copy.regions.voteFor(r.name)}</span>
                </button>
                <span className="block h-2 overflow-hidden rounded-[4px] bg-sunken" aria-hidden="true">
                  <span
                    className={`block h-full rounded-[4px] transition-[width] duration-1000 ease-out motion-reduce:transition-none ${lead ? 'bg-thread' : 'bg-thread/60'}`}
                    style={{ width: `${width(r.share, list)}%` }}
                  />
                </span>
              </li>
            )
          })}
        </ol>
        <p className="m-0 border-t border-rule px-5 py-4 font-medium">{note(list, late)}</p>
      </section>
    )
  }

  return (
    <>
      <PageHead
        title={copy.regions.heading}
        lead={copy.regions.lead}
        action={
          <a className="action" href={copy.regions.leaderboardURL}>
            {copy.regions.leaderboard}
          </a>
        }
      />
      {body}
      {done && (
        <p className={`m-0 mt-4 ${done.failed ? 'text-failed' : ''}`}>
          {done.failed ?? copy.regions.counted(done.name)}{' '}
          {!done.failed && (
            <a className="text-text underline decoration-thread underline-offset-4" href={copy.regions.leaderboardURL}>
              {copy.regions.leaderboard}
            </a>
          )}
        </p>
      )}
      <p className="m-0 mt-4 text-[14px] leading-5 text-secondary">{copy.regions.footnote}</p>
    </>
  )
}
